import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/vue-query';
import { request } from '@/api/request';
import { ApiError } from '@/api/errors';
import {
  mergeMutationOptions,
  mergeQueryOptions,
  type QueryOptions,
  type MutationOptions,
} from '@/modules/query/utils';
import { useFramework } from '@/modules/framework/api';
import { SUPPLIERS_QUERY } from '@/modules/connection-view-suppliers/api/queryKeys';
import {
  type Connection,
  ConnectionStatus,
  PipelineStatus,
} from '@@/packages/shared/types/models/connection';
import type { ClientAnswerNote } from '@@/packages/shared/types/models/clientAnswerNotes';
import type { ModificationType } from '@@/packages/shared/types/models/modification';
import type { ReassessmentStatus } from '@@/packages/shared/types/models/reassessment';
import type { RemediationRequest } from '@@/packages/shared/types/models/remediationRequest';
import type { ScopingAnswer } from '@@/packages/shared/types/models/scopingAnswer';
import { OrganisationOwnershipStatus } from '@@/packages/shared/types/models/organisation';
import type { LatestConnectionRequest } from '@@/packages/shared/types/models/connectionRequests';
import type {
  SupplierAnswerSet,
  SupplierAnswerSetsByKind,
} from '@@/packages/shared/types/models/answerSet';
import { PRIVATE_SUPPLIERS_QUERY } from '@/modules/settings/api/queryKeys';
import { CLIENT_DASHBOARD_QUERY } from '@/modules/client-dashboard/api/queryKeys';
import { useStore } from '@/store/useStore';
import {
  OWN_SUPPLIERS_QUERY,
  OWN_UNCONFIRMED_SUPPLIERS_QUERY,
} from '@/modules/security-profile-settings/api/queryKeys';
import { ControlParentRelationshipType } from '@@/packages/shared/types/models/control';
import { useClientOrSupplierProducts } from '@/modules/products/composables';
import { useCurrentOrg } from '@/modules/current-org/api';
import { useCurrentUser } from '@/modules/current-user/api';
import { useGenerateControlsWithAnswers } from '@/modules/framework/controls-with-answers';
import {
  fetchAnswerSetAnswers,
  fetchAnswerSetRequirements,
} from '@/modules/offline-assessment/api';
import {
  ANSWER_SET_ANSWERS_QUERY,
  ANSWER_SET_REQUIREMENTS_QUERY,
} from '@/modules/offline-assessment/api/queryKeys';
import { useTargetOrgType } from '../targetOrgType';
import { refreshSupplierScopeEventually } from './complianceConsistency';
import type { ComplianceByDomain, ComplianceByPolicy, SupplierControlComplianceV2 } from './types';
import {
  useClientInfo,
  useClientCompliance,
  type Answer as ClientAnswer,
  type ClientOverrideView,
} from './client';
import {
  SUPPLIER_INFO_QUERY,
  SUPPLIER_PUBLIC_INFO_QUERY,
  SUPPLIER_REASSESSMENT_STATUS_QUERY,
  SUPPLIER_EVIDENCE_QUERY,
  SUPPLIER_COMPLIANCE_VTWO_QUERY,
  SUPPLIER_ASSESSMENT_NOTES_QUERY,
  SUPPLIER_COMMUNITY_SIGNALS_QUERY,
  SUPPLIER_ASSESSMENT_ANSWERS_QUERY,
  SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
  SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY,
  SUPPLIER_ASSESSMENT_OVERRIDES_QUERY,
  CLIENT_ASSESSMENT_ANSWERS_QUERY,
  CLIENT_ASSESSMENT_SCOPING_ANSWERS_QUERY,
  CLIENT_ASSESSMENT_REMEDIATIONS_QUERY,
  CLIENT_ASSESSMENT_OVERRIDES_QUERY,
} from './queryKeys';

export enum ConnectionProfileType {
  UNCLAIMED_CONNECTION = 'UNCLAIMED_CONNECTION',
  BASIC_CONNECTION = 'BASIC_CONNECTION',
  FULL_CONNECTION = 'FULL_CONNECTION',
  MANAGED_CONNECTION = 'MANAGED_CONNECTION',
}

// ConsolidatedConnectionStatus is a summarized connection state derived from
// connection status, connection request status and whether an invite exists or not
export enum ConsolidatedConnectionStatus {
  RequestPending = 'CONNECTION_STATUS_REQUEST_PENDING', // connection request sent by the user
  Connected = 'CONNECTION_STATUS_CONNECTED', // full connection with a claimed supplier
  NotConnected = 'CONNECTION_STATUS_NOT_CONNECTED', // basic connection with a claimed supplier
  RequestRejected = 'CONNECTION_STATUS_REQUEST_REJECTED',
  Removed = 'CONNECTION_STATUS_REMOVED',
  Invited = 'CONNECTION_STATUS_INVITED',
  NotInvited = 'CONNECTION_STATUS_NOT_INVITED',
}

type CommunityStats = {
  totalConnections: number;
  approvals: number;
  risks: {
    open: number;
    closed: number;
  };
};

type Stats = {
  community?: ({
    communityID: string;
  } & CommunityStats)[];
  allCommunities?: CommunityStats & { controlSignals: number };
};

export type SupplierInfoResponse = {
  connectionID: string;
  orgInfo: {
    name: string;
    id: string;
    logoUrl: string;
    companyNumber: string;
    country: string;
    sector: string;
    tradingNames: string[];
  } | null;
  stats: Stats | null;
  tagsApplied: {
    confidentiality: string;
    criticality: string;
    pii: string;
  };
  pipelineStatus: PipelineStatus;
  initialAssessmentComplete: boolean;
  assessmentProgress: number;
  isPendingFrameworkRequirements: boolean;
  lastActiveAt: string | null;
  lastSignedInAt: string | null;
  lastSignedInState?: SupplierSignInState;
  assignedUsers: string[];
  isPrivate: boolean;
  connectionStatus: ConnectionStatus;
  ownershipStatus: OrganisationOwnershipStatus;
  latestConnectionRequest?: LatestConnectionRequest;
  // Null when no set resolves: an organisation that is not a supplier and has never answered.
  answerSet: SupplierAnswerSet | null;
  answerSetsByKind: SupplierAnswerSetsByKind;
};
type SupplierPublicInfoResponse = {
  id: string;
  name: string;
  companyNumber: string;
  country: string;
  streetAddress: string;
  postcode: string;
  city: string;
  sector: string;
  tradingNames: string[];
  website: string;
  connectionProfileType: string | null | undefined;
  isPrivate: boolean;
  connectionID: string;
  connectionStatus: ConnectionStatus;
  ownershipStatus: OrganisationOwnershipStatus;
  // The `/info` endpoint surfaces pipeline status, but only when a connection exists.
  pipelineStatus?: PipelineStatus;
  latestConnectionRequest?: LatestConnectionRequest;
};

export type Answer = {
  answer: string;
  answerID: string;
  controlID: string;
  productID?: string;
  answerSetID: string;
  documentIDs: string[];
  modifiedAt: string;
  notes: string;
  notApplicable: boolean;
  unconfirmedControlUpdate: boolean;
  archivedAt?: string;
  archivedBy?: string;
};

export type SupplierOverrideView = {
  id: string;
  controlID: string;
  connectionID: string;
  productID: string | null;
  type: ModificationType;
  createdAt: string;
};

export type GetSupplierComplianceResponseV2 = {
  appliedPolicyIDs: string[];
  compliancePercentage?: number;
  complianceByDomain?: Record<string, ComplianceByDomain>;
  controlCompliances?: SupplierControlComplianceV2[];
  complianceByPolicy?: Record<string, ComplianceByPolicy>;
};

type CommunitySignal = {
  controlID: string;
  remediations: number;
  risks: number;
};
type SupplierTags = {
  confidentiality: string;
  criticality: string;
  pii: string;
};

type SetAssignedUsersResponse = {
  assignedUsers: string[];
  createdAt: string;
};
export type SupplierEvidence = {
  id: string;
  filename: string;
  fileType: string;
  fileSize: number | null;
  createdAt: string;
  associatedType: string;
  associatedID: string;
  attachedTo: string;
};

type ToggleSupplierPrivacyResponse = {
  isPrivate: boolean;
  toggledPrivateAt: string;
  toggledPrivateBy: string;
};

type SuggestSupplierContactResponse = {
  submitted: boolean;
};

export type ProfileType = 'full' | 'basic' | 'unclaimed' | 'managed';

/**
 * How a supplier's last sign-in should be read. A missing date has two opposite
 * meanings, so the backend sends the reason alongside it: the supplier either
 * signed in before we began recording logins, or has never signed in since
 * claiming their profile. Absent entirely for unclaimed and managed profiles,
 * which have no users who sign in, and while the feature flag is off.
 */
export type SupplierSignInState = 'recorded' | 'before_records' | 'never';

async function fetchSupplierInfoResponse(supplierID: string) {
  const res = await request<SupplierInfoResponse>('GET', `/supplier/${supplierID}`);
  return res.data;
}

// Fetches the info, or joins a fetch already in flight, so a route guard decides
// on the connection as it is now rather than on a cached copy. The result lands
// in the cache, so the profile's tabs show the same.
export function fetchSupplierInfo(queryClient: QueryClient, supplierID: string) {
  return queryClient.fetchQuery({
    queryKey: [SUPPLIER_INFO_QUERY, supplierID],
    queryFn: () => fetchSupplierInfoResponse(supplierID),
  });
}

export function useSupplierInfo(supplierID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_INFO_QUERY, toRef(supplierID)] as const,
      queryFn: ({ queryKey }) => fetchSupplierInfoResponse(queryKey[1]),
    }),
  );

  return {
    ...query,
    supplierInfo: query.data,
    orgInfo: computed(() => query.data.value?.orgInfo),
    connectionID: computed(() => query.data.value?.connectionID),
    initialAssessmentComplete: computed(() => query.data.value?.initialAssessmentComplete),
    isPendingFrameworkRequirements: computed(
      () => query.data.value?.isPendingFrameworkRequirements,
    ),
    tagsApplied: computed(() => query.data.value?.tagsApplied),
    pipelineStatus: computed(() => query.data.value?.pipelineStatus),
    latestConnectionRequest: computed(() => query.data.value?.latestConnectionRequest ?? null),
    isPrivate: computed(() => query.data.value?.isPrivate),
    isFullConnection: computed(() => query.data.value?.connectionStatus === ConnectionStatus.Full),
    profileType: computed((): ProfileType => {
      if (query.data.value?.ownershipStatus === OrganisationOwnershipStatus.Managed) {
        return 'managed';
      }

      if (query.data.value?.ownershipStatus === OrganisationOwnershipStatus.Unclaimed) {
        return 'unclaimed';
      }
      return query.data.value?.connectionStatus === ConnectionStatus.Full ? 'full' : 'basic';
    }),
    ownershipStatus: computed(() => query.data.value?.ownershipStatus),
    lastSignedInAt: computed(() => query.data.value?.lastSignedInAt ?? null),
    lastSignedInState: computed(() => query.data.value?.lastSignedInState),
    answerSet: computed(() => query.data.value?.answerSet),
    answerSetsByKind: computed(() => query.data.value?.answerSetsByKind),
  };
}

export function usePublicSupplierInfo(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_PUBLIC_INFO_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<SupplierPublicInfoResponse>(
          'GET',
          `/supplier/${queryKey[1]}/info`,
        );
        return res.data;
      },
      // Opts out of the app-wide `keepPreviousData` (plugins/vueQuery.ts): the key
      // is which supplier this is, so across a key change the page would draw the
      // previous supplier's profile, and start its child requests, under the next
      // supplier's id.
      placeholderData: undefined,
    }),
  );

  const { user, isLoading: isCurrentUserLoading } = useCurrentUser();

  const isForbidden = computed(
    () => query.error.value instanceof ApiError && query.error.value.status === 403,
  );

  return {
    ...query,
    supplierInfo: query.data,
    isConnected: computed(() => !!query.data.value?.connectionProfileType),
    isFullConnection: computed(() => query.data.value?.connectionStatus === ConnectionStatus.Full),
    // The restrictor refuses before doing any of the endpoint's work, so its 403
    // can beat /user back on a warm start, and a 403 cannot be classified without
    // the viewer's restriction flag. Folded in here so a consumer cannot render a
    // verdict that is not in yet.
    isLoading: computed(
      () => query.isLoading.value || (isForbidden.value && isCurrentUserLoading.value),
    ),
    isNotFound: computed(
      () => query.error.value instanceof ApiError && query.error.value.status === 404,
    ),
    // Inferred from the status rather than a reason code: the restrictor
    // middleware answers with a bare 403, and the endpoint's only other 403 is
    // the ClientsSuppliersView gate, which AppContent refuses the route on
    // before this query runs (FEDX-372).
    isRestricted: computed(() => isForbidden.value && user.value?.restrictSupplierAccess === true),
    ownershipStatus: computed(() => query.data.value?.ownershipStatus),
    latestConnectionRequest: computed(() => query.data.value?.latestConnectionRequest ?? null),
  };
}

export function useSupplierReassessmentStatus(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { ownershipStatus, isFullConnection } = useSupplierInfo(supplierID, options);

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_REASSESSMENT_STATUS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ReassessmentStatus>('GET', `/reassessment/status/${queryKey[1]}`);
        return res.data;
      },
      enabled: () =>
        ownershipStatus.value &&
        ownershipStatus.value !== 'managed' &&
        isFullConnection.value === true,
    }),
  );

  return {
    ...query,
    reassessmentStatus: query.data,
    status: computed(() => query.data.value?.status),
  };
}

export function useSupplierEvidence(supplierID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const { isFullConnection } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_EVIDENCE_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<SupplierEvidence[]>('GET', `/suppliers/${queryKey[1]}/evidences`);
        return res.data;
      },
      // The endpoint returns 403 for a non-full connection
      enabled: () => isFullConnection.value === true,
    }),
  );

  const evidence = computed(() => query.data.value || []);

  const { domainWithID, controlWithID } = useFramework();
  // oxlint-disable-next-line no-use-before-define
  const { answersByAnswerID } = useSupplierAssessmentAnswers(supplierID);

  const store = useStore();

  const supplierEvidence = computed(() =>
    evidence.value.map((evidenceItem) => {
      const answer = answersByAnswerID.value[evidenceItem.associatedID];
      const control = answer ? controlWithID(answer.controlID) : undefined;
      const domain = control ? domainWithID(control.domainID) : undefined;

      // Set answer expiry date if present
      let expiryAnswer: any;
      try {
        if (answer) {
          expiryAnswer = JSON.parse(answer.answer);
        }
        // oxlint-disable-next-line @typescript-eslint/no-unused-vars, no-empty
      } catch (e) {}

      return {
        ...evidenceItem,
        size: store.getters['uploads/files/getFileSize'](evidenceItem) as number | null,
        control:
          control && domain
            ? {
                id: control.controlID,
                number: control.number,
                letter: domain.letter,
                domainID: control.domainID,
                domainName: domain.name,
              }
            : undefined,
        expiryDate: expiryAnswer?.expiryDate,
      };
    }),
  );

  return { ...query, supplierEvidence };
}

export function useSupplierCompliance(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_COMPLIANCE_VTWO_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<GetSupplierComplianceResponseV2>(
          'GET',
          `/suppliers/${queryKey[1]}/compliance/v2`,
        );
        return res.data;
      },
    }),
  );

  function getAnswerCompliance(controlID: string, productID: string | null) {
    return query.data.value?.controlCompliances?.find(
      (cc) => cc.controlID === controlID && cc.productID === productID,
    );
  }

  return {
    ...query,
    query,
    getAnswerCompliance,
    compliance: query.data,
  };
}

export function useSupplierAssessmentClientNotes(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { isFullConnection, answerSet } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSESSMENT_NOTES_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ClientAnswerNote[]>(
          'GET',
          `/supplier/${queryKey[1]}/assessment/client-answer-notes`,
        );
        return res.data;
      },
      enabled: () => isFullConnection.value === true || answerSet.value?.kind === 'client',
    }),
  );

  const clientNotes = computed(() => query.data.value || []);

  const clientNotesByControlID = computed(() =>
    Object.groupBy(clientNotes.value, (item) => item.controlID),
  );

  function getClientNote(controlID: string, productID?: string) {
    return clientNotesByControlID.value[controlID]?.find(
      (note) =>
        note.controlID === controlID &&
        // If one is null and the other is undefined, return true
        ((!note.productID && !productID) || note.productID === productID),
    );
  }

  return {
    ...query,
    clientNotes,
    getClientNote,
  };
}

export function useSupplierAssessmentCommunitySignals(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_COMMUNITY_SIGNALS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<CommunitySignal[]>(
          'GET',
          `/supplier/${queryKey[1]}/community-signals`,
        );
        return res.data;
      },
    }),
  );

  const communitySignals = computed(() => query.data.value ?? []);

  function getSignals(controlID: string) {
    const signal = communitySignals.value.find((s) => s.controlID === controlID);
    return {
      risks: signal?.risks || 0,
      remediations: signal?.remediations || 0,
    };
  }

  function getHasSignals(controlID: string): boolean {
    const signal = getSignals(controlID);
    return !!(signal.risks || signal.remediations);
  }

  return {
    ...query,
    getSignals,
    getHasSignals,
  };
}

export function useUpdateAssignedUsers(
  options?: MutationOptions<SetAssignedUsersResponse, { supplierID: string; users: string[] }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID, users }) {
        const res = await request<SetAssignedUsersResponse>(
          'PUT',
          `/suppliers/${supplierID}/user-assignments`,
          {
            users,
          },
        );
        return res.data;
      },
      async onSuccess(data, variables) {
        await queryClient.setQueryData(
          [SUPPLIER_INFO_QUERY, variables.supplierID],
          (oldData: SupplierInfoResponse | undefined) =>
            oldData
              ? ({
                  ...oldData,
                  assignedUsers: data.assignedUsers,
                } satisfies SupplierInfoResponse)
              : undefined,
        );
      },
    }),
  );
}

export function useToggleSupplierPrivacy(
  options?: MutationOptions<ToggleSupplierPrivacyResponse, { id: string }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ id }) {
        const res = await request<ToggleSupplierPrivacyResponse>(
          'POST',
          `/suppliers/${id}/toggle-privacy`,
        );
        return res.data;
      },
      async onSuccess(data, { id }) {
        // update the supplier info store
        // on the client side
        await queryClient.setQueryData(
          [SUPPLIER_INFO_QUERY, id],
          (oldData: SupplierInfoResponse) => ({
            ...oldData,
            isPrivate: data.isPrivate,
          }),
        );
        // also update the settings list by invalidating the query
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [PRIVATE_SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [OWN_SUPPLIERS_QUERY] }),
        ]);
      },
    }),
  );
}

export function useSuggestSupplierContact(
  options?: MutationOptions<
    SuggestSupplierContactResponse,
    { supplierID: string; contactName: string; contactEmail: string; contactDescription: string }
  >,
) {
  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ supplierID, contactName, contactEmail, contactDescription }) {
        const res = await request<SuggestSupplierContactResponse>(
          'POST',
          `/suppliers/${supplierID}/contact-suggestions`,
          { contactName, contactEmail, contactDescription },
        );
        return res.data;
      },
    }),
  );
}

export function useRemoveConnection(
  options?: MutationOptions<
    Connection,
    { connectionID: string; organisationID: string; message: string }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn({ connectionID, message }) {
        const res = await request<Connection>('DELETE', `/org/connections/${connectionID}`, {
          removedMessage: message,
        });
        return res.data;
      },
      async onSuccess(_data, variables) {
        await queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] });
        queryClient.removeQueries({ queryKey: [SUPPLIER_INFO_QUERY, variables.organisationID] });
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_PUBLIC_INFO_QUERY, variables.organisationID],
          }),
          queryClient.invalidateQueries({ queryKey: [OWN_SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [OWN_UNCONFIRMED_SUPPLIERS_QUERY] }),
        ]);
      },
    }),
  );
}

export function useUpdateSupplierTags(
  options?: MutationOptions<SupplierTags, { supplierID: string; tags: SupplierTags }>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(variables) {
        const res = await request<SupplierTags>(
          'PUT',
          `/suppliers/${variables.supplierID}/tags-applied`,
          variables.tags,
        );
        return res.data;
      },
      async onSuccess(_data, variables) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [CLIENT_DASHBOARD_QUERY] }),
          queryClient.invalidateQueries({ queryKey: [OWN_SUPPLIERS_QUERY] }),
        ]);

        refreshSupplierScopeEventually(queryClient, variables.supplierID);
      },
    }),
  );
}

export function useUpdatePipelineStatus(
  options?: MutationOptions<
    Connection,
    { connectionID: string; supplierID: string; pipelineStatus: PipelineStatus }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(variables) {
        const res = await request<Connection>(
          'PUT',
          `/connections/${variables.connectionID}/pipeline-status`,
          { pipelineStatus: variables.pipelineStatus },
        );
        return res.data;
      },
      async onSuccess(_data, variables) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_INFO_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({
            queryKey: [SUPPLIER_PUBLIC_INFO_QUERY, variables.supplierID],
          }),
          queryClient.invalidateQueries({ queryKey: [SUPPLIERS_QUERY] }),
        ]);
      },
    }),
  );
}

export function useClientOrSupplierInfo(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const { clientInfo, isLoading: isLoadingClient } = useClientInfo(orgID, {
    enabled: () =>
      toValue(type) === 'client' &&
      Boolean(toValue(orgID)) &&
      (!options?.enabled || options.enabled() === true),
  });
  const {
    supplierInfo,
    isLoading: isLoadingSupplier,
    isPrivate: isSupplierPrivate,
    profileType: supplierProfileType,
    answerSet: supplierAnswerSet,
    isFullConnection: isSupplierFullConnection,
  } = useSupplierInfo(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' &&
      Boolean(toValue(orgID)) &&
      (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? isLoadingClient.value : isLoadingSupplier.value,
  );

  const connectionID = computed(() =>
    toValue(type) === 'client' ? clientInfo.value?.connectionID : supplierInfo.value?.connectionID,
  );
  const orgInfo = computed(() =>
    toValue(type) === 'client' ? clientInfo.value?.orgInfo : supplierInfo.value?.orgInfo,
  );
  const initialAssessmentComplete = computed(() =>
    toValue(type) === 'client'
      ? clientInfo.value?.initialAssessmentComplete
      : supplierInfo.value?.initialAssessmentComplete,
  );
  const assessmentProgress = computed(() =>
    toValue(type) === 'client'
      ? clientInfo.value?.assessmentProgress
      : supplierInfo.value?.assessmentProgress,
  );

  const lastActiveAt = computed(() =>
    toValue(type) === 'client' ? clientInfo.value?.lastActiveAt : supplierInfo.value?.lastActiveAt,
  );

  const profileType = computed(() =>
    toValue(type) === 'client' ? null : supplierProfileType.value,
  );

  const ownershipStatus = computed(() => supplierInfo.value?.ownershipStatus);

  // null if client
  const isPrivate = computed(() => (toValue(type) === 'client' ? null : isSupplierPrivate.value));

  return {
    isLoading,
    connectionID,
    orgInfo,
    initialAssessmentComplete,
    assessmentProgress,
    lastActiveAt,
    isPrivate,
    profileType,
    ownershipStatus,
    answerSet: computed(() => (toValue(type) === 'supplier' ? supplierAnswerSet.value : undefined)),
    isFullConnection: computed(() =>
      toValue(type) === 'supplier' ? isSupplierFullConnection.value : undefined,
    ),
  };
}

export function useClientOrSupplierCompliance(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const clientQuery = useClientCompliance(orgID, {
    enabled: () => toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const supplierQuery = useSupplierCompliance(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const compliance = computed(
    () => (toValue(type) === 'client' ? clientQuery : supplierQuery).compliance.value,
  );

  const controlCompliances = computed(() => compliance.value?.controlCompliances ?? []);

  const controlCompliancesByControlID = computed(() =>
    Object.groupBy(controlCompliances.value, (item) => item.controlID),
  );

  function getAnswerComplianceStatus(controlID: string, productID: string | null) {
    const controlCompliance = controlCompliancesByControlID.value[controlID]?.find(
      (cc) => cc.controlID === controlID && cc.productID === productID,
    );
    return controlCompliance?.complianceStatus;
  }

  function getPolicyRequirement(controlID: string) {
    const controlCompliance = controlCompliances.value.find((cc) => cc.controlID === controlID);
    return controlCompliance?.strictestRequirement;
  }

  return {
    clientQuery,
    supplierQuery,
    isLoading,
    isError,
    compliance,
    controlCompliances,
    controlCompliancesByControlID,
    getPolicyRequirement,
    getAnswerComplianceStatus,
  };
}

function useClientAssessmentScopingAnswers(
  clientID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const currentOrg = useCurrentOrg({ enabled: toValue(options)?.enabled });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSESSMENT_SCOPING_ANSWERS_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ScopingAnswer[]>(
          'GET',
          `/client/${queryKey[1]}/assessment/answers/scoping`,
        );
        return res.data;
      },
      enabled: () => currentOrg.data.value?.initialAssessmentComplete,
    }),
  );

  return {
    ...query,
    scopingAnswers: computed(() => query.data.value || []),
  };
}

function useSupplierAssessmentScopingAnswers(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { isFullConnection, initialAssessmentComplete, answerSet } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  // The supplier endpoint serves only the supplier's own scoping, and refuses a
  // basic connection, so a client set's scoping is read from the set itself.
  const clientSetID = computed(() =>
    answerSet.value?.kind === 'client' ? answerSet.value.id : undefined,
  );

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [
        SUPPLIER_ASSESSMENT_SCOPING_ANSWERS_QUERY,
        toRef(supplierID),
        clientSetID,
      ] as const,
      async queryFn({ queryKey: [, id, setID] }) {
        if (setID) {
          const res = await request<{ scopingAnswers: ScopingAnswer[] }>(
            'GET',
            `/assessments/answer-sets/${setID}/scoping-answers`,
          );
          return res.data.scopingAnswers;
        }
        const res = await request<ScopingAnswer[]>(
          'GET',
          `/supplier/${id}/assessment/answers/scoping`,
        );
        return res.data;
      },
      // Like the answers, a client set's scoping is readable before it is completed.
      enabled: () =>
        !!clientSetID.value ||
        (isFullConnection.value === true && initialAssessmentComplete.value === true),
    }),
  );

  return {
    ...query,
    scopingAnswers: computed(() => query.data.value || []),
  };
}

export function useClientOrSupplierAssessmentScopingAnswers(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const clientQuery = useClientAssessmentScopingAnswers(orgID, {
    enabled: () => toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const supplierQuery = useSupplierAssessmentScopingAnswers(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const scopingAnswers = computed(
    () => (toValue(type) === 'client' ? clientQuery : supplierQuery).scopingAnswers.value,
  );

  const scopingAnswersByDomainID = computed(() =>
    Object.fromEntries(scopingAnswers.value.map((sa) => [sa.domainID, sa])),
  );

  const { domainWithID, controlWithID } = useFramework();
  // TODO: stop making weird hybrid objects and just look up the domain info where it's required
  const scopingAnswersWithExtrasByDomainID = computed(() =>
    Object.fromEntries(
      scopingAnswers.value.map((sa) => {
        const domain = domainWithID(sa.domainID);
        return [
          sa.domainID,
          {
            ...sa,
            letter: domain.letter,
            scopingQuestion: domain.scopingQuestion,
          },
        ];
      }),
    ),
  );

  function isDomainOutOfScope(domainID: string) {
    const domain = domainWithID(domainID);
    if (!domain.scopingQuestion) return false;
    const scopingAnswer = scopingAnswersByDomainID.value?.[domain?.domainID];
    return !!scopingAnswer && !scopingAnswer.answer;
  }

  // An answer is out of scope when its domain was scoped out, or when a scoping
  // parent made it not applicable. None of it is shown to the client: not the
  // answer, its notes, its evidence, or its dates.
  function isAnswerOutOfScope(controlID: string, answer?: Answer | ClientAnswer) {
    const control = controlWithID(controlID);
    return (
      isDomainOutOfScope(control.domainID) ||
      (control.parentRelationshipType === ControlParentRelationshipType.Scoping &&
        !!answer?.notApplicable)
    );
  }

  return {
    clientQuery,
    supplierQuery,
    isLoading,
    isError,
    scopingAnswers,
    scopingAnswersByDomainID,
    scopingAnswersWithExtrasByDomainID,
    isDomainOutOfScope,
    isAnswerOutOfScope,
  };
}

function useSupplierAssessmentRemediations(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { isFullConnection } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSESSMENT_REMEDIATIONS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<RemediationRequest[]>(
          'GET',
          `/supplier/${queryKey[1]}/assessment/remediations`,
        );
        return res.data;
      },
      // The endpoint refuses anything short of a full connection.
      enabled: () => isFullConnection.value === true,
    }),
  );

  return {
    ...query,
    remediations: computed(() => query.data.value || []),
  };
}

function useClientAssessmentRemediations(
  clientID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSESSMENT_REMEDIATIONS_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<RemediationRequest[]>(
          'GET',
          `/client/${queryKey[1]}/assessment/remediations`,
        );
        return res.data;
      },
    }),
  );

  return {
    ...query,
    remediations: computed(() => query.data.value || []),
  };
}

export function useClientOrSupplierAssessmentRemediations(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const clientQuery = useClientAssessmentRemediations(orgID, {
    enabled: () => toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const supplierQuery = useSupplierAssessmentRemediations(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const remediations = computed(
    () => (toValue(type) === 'client' ? clientQuery : supplierQuery).remediations.value,
  );

  const remediationsByControlID = computed(() =>
    Object.groupBy(remediations.value, (item) => item.controlID),
  );

  function getRemediation(controlID: string, productID: string | null) {
    return remediationsByControlID.value[controlID]?.find(
      (r) => r.controlID === controlID && r.productID === productID,
    );
  }

  // Count of remediations that have not been responded to, and are not cancelled or completed
  const remediationsPendingCount = computed(
    () => remediations.value.filter((r) => !r.responded && !r.cancelled && !r.completed).length,
  );

  // Count of remediations that are not completed but have been responded to.
  const remediationResponsesCount = computed(
    () => remediations.value.filter((r) => r.responded && !r.cancelled && !r.completed).length,
  );

  return {
    clientQuery,
    supplierQuery,
    isLoading,
    isError,
    remediations,
    remediationsPendingCount,
    remediationResponsesCount,
    getRemediation,
  };
}

function useClientAssessmentAnswers(clientID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const currentOrg = useCurrentOrg({ enabled: toValue(options)?.enabled });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSESSMENT_ANSWERS_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ClientAnswer[]>(
          'GET',
          `/client/${queryKey[1]}/assessment/answers`,
        );
        return res.data;
      },
      enabled: () => currentOrg.data.value?.initialAssessmentComplete,
    }),
  );

  return {
    ...query,
    answers: computed(() => query.data.value || []),
  };
}

export function useSupplierAssessmentAnswers(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { isFullConnection, initialAssessmentComplete, answerSet } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  // The legacy endpoint fills evidence from the supplier's documents, so a client
  // set, whose evidence is the client's, is read from the set itself.
  const clientSetID = computed(() =>
    answerSet.value?.kind === 'client' ? answerSet.value.id : undefined,
  );

  const queryClient = useQueryClient();

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSESSMENT_ANSWERS_QUERY, toRef(supplierID), clientSetID] as const,
      async queryFn({ queryKey: [, id, setID] }) {
        if (setID) {
          // Read through the answering view's cache entries, which its writes
          // invalidate along with this query.
          const [setAnswers, requirements] = await Promise.all([
            queryClient.fetchQuery({
              queryKey: [ANSWER_SET_ANSWERS_QUERY, setID],
              queryFn: () => fetchAnswerSetAnswers(setID),
            }),
            queryClient.fetchQuery({
              queryKey: [ANSWER_SET_REQUIREMENTS_QUERY, setID],
              queryFn: () => fetchAnswerSetRequirements(setID),
            }),
          ]);
          // The set holds every live answer, where the legacy endpoint returns only
          // the controls the connection requires.
          const requiredControlIDs = new Set(requirements.controls.map((c) => c.controlID));
          return setAnswers
            .filter((answer) => requiredControlIDs.has(answer.controlID))
            .map((answer): Answer => ({
              answer: answer.answer,
              answerID: answer.answerID,
              controlID: answer.controlID,
              answerSetID: setID,
              documentIDs: answer.documentIDs,
              modifiedAt: answer.modifiedAt,
              notes: answer.notes,
              notApplicable: answer.notApplicable,
              unconfirmedControlUpdate: answer.unconfirmedControlUpdate,
            }));
        }
        const res = await request<Answer[]>('GET', `/supplier/${id}/assessment/answers`);
        return res.data;
      },
      // The client wrote a client-managed assessment's answers, so they are readable
      // on any connection and before the assessment is completed.
      enabled: () =>
        !!clientSetID.value ||
        (isFullConnection.value === true && initialAssessmentComplete.value === true),
    }),
  );

  // Like the legacy endpoint, a client set leaves out deprecated controls and domains.
  const { controls, domainWithID } = useFramework();
  const liveControlIDs = computed(
    () =>
      new Set(
        controls.value
          .filter((control) => !control.deprecated && !domainWithID(control.domainID).deprecated)
          .map((control) => control.controlID),
      ),
  );

  const answers = computed(() => {
    const data = query.data.value || [];
    return clientSetID.value
      ? data.filter((answer) => liveControlIDs.value.has(answer.controlID))
      : data;
  });
  const answersByAnswerID = computed(() =>
    Object.fromEntries(answers.value.map((answer) => [answer.answerID, answer])),
  );

  return {
    ...query,
    answers,
    answersByAnswerID,
  };
}

interface AnswerWithFrameworkDetails extends ClientAnswer, Answer {
  letter: string;
  domainID: string;
  number: number;
  question: string;
}

export function useClientOrSupplierAssessmentAnswers(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const clientQuery = useClientAssessmentAnswers(orgID, {
    enabled: () => toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const supplierQuery = useSupplierAssessmentAnswers(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const refetch = computed(() =>
    toValue(type) === 'client' ? clientQuery.refetch : supplierQuery.refetch,
  );

  const answers = computed(
    () => (toValue(type) === 'client' ? clientQuery : supplierQuery).answers.value,
  );

  const { controls, controlWithID, domainWithID, sortedControlsDeprecated } = useFramework();

  const answersWithFrameworkDetails = computed<AnswerWithFrameworkDetails[]>(() =>
    answers.value.map((answer: ClientAnswer | Answer) => {
      const control = controlWithID(answer.controlID);
      const domain = domainWithID(control.domainID);

      return {
        ...answer,
        letter: domain.letter,
        domainID: control.domainID,
        number: control.number,
        question: control.question,
      };
    }),
  );

  const answersByControlID = computed(() =>
    Object.groupBy(answersWithFrameworkDetails.value, (answer) => answer.controlID),
  );

  /** @deprecated use answersByControlID instead */
  const orgAnswersOnlyByControlID = computed(() =>
    Object.fromEntries(
      answersWithFrameworkDetails.value
        .filter((answer) => !answer.productID)
        .map((answer) => [answer.controlID, answer]),
    ),
  );

  const answersByAnswerID = computed(() => {
    if (!answersWithFrameworkDetails.value) return {};
    return Object.fromEntries(
      answersWithFrameworkDetails.value.map((answer) => [answer.answerID, answer]),
    );
  });

  const { generateControlsWithAnswers } = useGenerateControlsWithAnswers();

  const { products } = useClientOrSupplierProducts(orgID, options);

  const controlsWithAnswers = computed(() =>
    generateControlsWithAnswers(
      controls.value.map((control) => control.controlID),
      answersByControlID.value,
      products.value.map((product) => product.id),
    ),
  );

  // Get sorted deprecated controls that have been answered.
  // Returns a list of controlIDs.
  const sortedDeprecatedControlAnswers = computed(() =>
    sortedControlsDeprecated.value
      .map((controlObj) => controlObj.controlID)
      .filter((c) => !!answersByControlID.value[c]),
  );

  return {
    clientQuery,
    supplierQuery,
    isLoading,
    isError,
    refetch,
    answers,
    answersByAnswerID,
    controlsWithAnswers,
    answersByControlID,
    orgAnswersOnlyByControlID,
    sortedDeprecatedControlAnswers,
  };
}

function useSupplierAssessmentOverrides(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const { isFullConnection } = useSupplierInfo(supplierID, {
    enabled: () => toValue(toValue(options)?.enabled),
  });

  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSESSMENT_OVERRIDES_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<SupplierOverrideView[]>(
          'GET',
          `/supplier/${queryKey[1]}/assessment/overrides`,
        );
        return res.data;
      },
      enabled: () => isFullConnection.value === true,
    }),
  );

  const overrides = computed(() => query.data.value || []);

  const overridesByControlID = computed(() =>
    Object.groupBy(overrides.value, (override) => override.controlID),
  );

  return {
    ...query,
    overrides,
    overridesByControlID,
  };
}

function useClientAssessmentOverrides(clientID: MaybeRefOrGetter<string>, options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSESSMENT_OVERRIDES_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await request<ClientOverrideView[]>(
          'GET',
          `/client/${queryKey[1]}/assessment/overrides`,
        );
        return res.data;
      },
    }),
  );

  const overrides = computed(() => query.data.value || []);

  const overridesByControlID = computed(() =>
    Object.groupBy(overrides.value, (override) => override.controlID),
  );

  return {
    ...query,
    overrides,
    overridesByControlID,
  };
}

export function useClientOrSupplierAssessmentOverrides(
  orgID: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean;
    type?: MaybeRefOrGetter<'client' | 'supplier'>;
  },
) {
  const type = useTargetOrgType(options?.type);
  const clientQuery = useClientAssessmentOverrides(orgID, {
    enabled: () => toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });
  const supplierQuery = useSupplierAssessmentOverrides(orgID, {
    enabled: () =>
      toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const overrides = computed<SupplierOverrideView[] | ClientOverrideView[]>(() =>
    toValue(type) === 'client' ? clientQuery.overrides.value : supplierQuery.overrides.value,
  );

  const overridesByControlID = computed(() =>
    Object.groupBy(overrides.value, (override) => override.controlID),
  );

  function getOverrideByControlAndProductID(
    controlID: string,
    productID: string | null,
  ): SupplierOverrideView | ClientOverrideView | undefined {
    return overridesByControlID.value[controlID]?.find(
      (override) => override.productID === productID,
    );
  }

  const exemptionCount = computed(
    () => overrides.value.filter((override) => override.type === 'exemption').length,
  );

  const nonCompliancesCount = computed(
    () => overrides.value.filter((override) => override.type === 'noncompliance').length,
  );

  return {
    isLoading,
    isError,
    overrides,
    overridesByControlID,
    getOverrideByControlAndProductID,
    exemptionCount,
    nonCompliancesCount,
  };
}
